import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Target, Zap, BarChart3, Users, ArrowRight, Check, 
  MessageSquare, Bot, TrendingUp, Mail, Shield, 
  ChevronRight, Play, Star, FileText, Layers, Inbox, Globe,
  Calendar, FlaskConical, CreditCard, ChevronDown, Palette,
  MousePointer2, BellRing, LineChart, X, Send, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Robot Asset URLs
const ROBOTS = {
  mascot: "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/67fab65f02be6b766ed7e9a5a868d2e9a2f5db32d2c9edb603c968ef09ab7cd2.png",
  leadCapture: "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/b8022331cb2a6bba830b85b07f410a2d21d08b4a0baf13ee1630a1104b122d9c.png",
  automation: "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/3eb1d0dc2348260311b55b76dadfcf4570f15b3541abdb3e3250bddd816bbdad.png",
  communication: "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/2136401cd906a6b2fef9a15a33a5274e2c642e961fc643739f2be89f63c15075.png",
  analytics: "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/993712b7156b8cbad0f877105fdc3cd479281184025be01c31db85e9b775caa6.png",
  dashboard: "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/0ba90f0e634908c63cd41734ac1c2615465f8438a8338b8b7830edbdcf44a282.png",
  mobile: "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/165eb553864452ee52ef06940f2597bec68dab63d91181bd15e45285d1aec35c.png"
};

// Chatbot Lead Capture Widget Component
const ChatbotWidget = ({ onClose, onSubmit }) => {
  const [step, setStep] = useState('form'); // 'form', 'chat', 'success'
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Create lead via API
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await fetch(`${BACKEND_URL}/api/leads`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: '',
            industry: '',
            tags: ['chatbot-lead', 'website']
          })
        });
      }
    } catch (error) {
      console.log('Lead capture (guest mode):', formData);
    }

    setStep('chat');
    setMessages([
      { type: 'bot', text: `Hi ${formData.name.split(' ')[0]}! 👋 Welcome to AI Lead Mantra!` },
      { type: 'bot', text: `I'm Aria, your AI assistant. How can I help you today?` }
    ]);
    
    if (onSubmit) onSubmit(formData);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    const userMsg = inputMessage;
    setMessages(prev => [...prev, { type: 'user', text: userMsg }]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      setIsTyping(false);
      let response = "Thanks for your message! Our team will get back to you shortly. In the meantime, feel free to explore our features or start your free trial!";
      
      if (userMsg.toLowerCase().includes('price') || userMsg.toLowerCase().includes('cost')) {
        response = "Great question! We have 3 plans: Starter (₹1,499/mo), Growth (₹3,999/mo), and Agency (₹9,999/mo). All come with a 14-day free trial! Would you like me to explain what's included?";
      } else if (userMsg.toLowerCase().includes('demo') || userMsg.toLowerCase().includes('trial')) {
        response = "Absolutely! You can start your 14-day free trial right now - no credit card required. Just click 'Get Started' in the top menu. Want me to walk you through the key features?";
      } else if (userMsg.toLowerCase().includes('whatsapp')) {
        response = "Yes! We support WhatsApp automation for instant messaging with automated campaigns. It's perfect for follow-ups!";
      }
      
      setMessages(prev => [...prev, { type: 'bot', text: response }]);
    }, 1500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      className="fixed bottom-24 right-6 w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-200"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0a5c44] to-[#0d7c5c] p-4 flex items-center gap-3">
        <div className="relative">
          <img src={ROBOTS.mascot} alt="Aria" className="w-12 h-12 rounded-full border-2 border-white/30 object-cover bg-white" />
          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></span>
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold text-lg">AI Lead Mantra</h3>
          <p className="text-emerald-200 text-sm flex items-center gap-1">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            Aria is online
          </p>
        </div>
        <button onClick={onClose} className="text-white/80 hover:text-white p-1">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="bg-gray-50">
        {step === 'form' ? (
          <div className="p-5">
            <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border-l-4 border-emerald-500">
              <p className="text-gray-800 flex items-center gap-2">
                <span className="text-xl">👋</span>
                <span className="font-medium">Welcome!</span>
              </p>
            </div>
            
            <p className="text-gray-600 mb-4 text-sm">Please provide your details to start the conversation:</p>
            
            <form onSubmit={handleFormSubmit} className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Name</label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Phone</label>
                <div className="flex">
                  <div className="flex items-center gap-1 px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                    <span className="text-lg">🇮🇳</span>
                    <span className="text-gray-600 text-sm">+91</span>
                  </div>
                  <input
                    type="tel"
                    placeholder="9876543210"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-[#0a5c44] text-white py-3 rounded-lg font-semibold hover:bg-[#0d7c5c] transition-colors"
              >
                Start Chat
              </button>
            </form>
          </div>
        ) : (
          <div className="flex flex-col h-[350px]">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                    msg.type === 'user' 
                      ? 'bg-[#0a5c44] text-white rounded-br-md' 
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                  }`}>
                    {msg.text}
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Input */}
            <div className="p-3 bg-white border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  onClick={handleSendMessage}
                  className="w-10 h-10 bg-[#0a5c44] text-white rounded-full flex items-center justify-center hover:bg-[#0d7c5c] transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Powered by */}
      <div className="bg-gray-100 px-4 py-2 text-center">
        <span className="text-xs text-gray-500">Powered by <span className="font-semibold text-[#0a5c44]">AI Lead Mantra</span></span>
      </div>
    </motion.div>
  );
};

const LandingPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatbotPulse, setChatbotPulse] = useState(true);

  useEffect(() => {
    // Chatbot pulse effect only - no auto-popup
    setChatbotPulse(false);
  }, []);

  const handleGoogleLogin = () => {
    window.location.href = `${BACKEND_URL}/api/auth/google/login`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const endpoint = authMode === 'login' ? '/api/auth/login' : '/api/auth/signup';
      const body = authMode === 'login' 
        ? { email, password }
        : { email, password, name };
      
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const data = await res.json();
      
      if (res.ok && data.token) {
        localStorage.setItem('auth_token', data.token);
        window.location.href = '/dashboard';
      } else {
        alert(data.detail || 'Authentication failed');
      }
    } catch (error) {
      alert('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Robot-powered features
  const robotFeatures = [
    { 
      robot: ROBOTS.leadCapture,
      title: 'AI Lead Capture',
      subtitle: 'Smart Form Bot',
      description: 'Our Lead Capture Robot automatically qualifies and scores leads using AI, so you focus on hot prospects only.',
      highlights: ['Auto Lead Scoring', 'Smart Forms', 'CSV Import']
    },
    { 
      robot: ROBOTS.automation,
      title: 'Automation Engine',
      subtitle: 'Workflow Bot',
      description: 'Multi-tasking Automation Robot handles your workflows 24/7 - sending emails, WhatsApp messages, and more.',
      highlights: ['Visual Builder', 'Multi-channel', 'Trigger-based']
    },
    { 
      robot: ROBOTS.communication,
      title: 'Omni-Channel Bot',
      subtitle: 'Communication Hub',
      description: 'Communication Robot manages all your conversations across WhatsApp, Email, and SMS from one unified inbox.',
      highlights: ['WhatsApp API', 'Email Sequences', 'Unified Inbox']
    },
    { 
      robot: ROBOTS.analytics,
      title: 'Analytics AI',
      subtitle: 'Insights Bot',
      description: 'Analytics Robot provides real-time insights, revenue tracking, and predictive recommendations.',
      highlights: ['Live Dashboard', 'Revenue Tracking', 'AI Predictions']
    },
  ];

  const stats = [
    { value: '10,000+', label: 'Leads Captured', icon: <Users className="w-5 h-5" /> },
    { value: '95%', label: 'Satisfaction Rate', icon: <Star className="w-5 h-5" /> },
    { value: '₹2Cr+', label: 'Revenue Generated', icon: <TrendingUp className="w-5 h-5" /> },
    { value: '500+', label: 'AI Robots Active', icon: <Bot className="w-5 h-5" /> },
  ];

  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    { 
      name: 'Starter', 
      robot: '1 AI Robot',
      monthlyPrice: '₹3,999',
      yearlyPrice: '₹39,990',
      description: 'Perfect for small businesses getting started with AI-powered lead management.',
      features: ['500 leads/month', '5,000 AI tokens', 'Email automation', 'Basic analytics', '5 landing pages'],
      cta: 'Get Starter',
      ctaStyle: 'outline'
    },
    { 
      name: 'Growth', 
      robot: '4 AI Robots',
      monthlyPrice: '₹6,999',
      yearlyPrice: '₹69,990',
      description: 'Ideal for growing teams who need advanced automation and multi-channel outreach.',
      features: ['5,000 leads/month', '50,000 AI tokens', 'WhatsApp + Email', 'Advanced analytics', 'Unlimited funnels', 'A/B testing', 'Team access (3)'],
      featured: true,
      cta: 'Get Growth',
      ctaStyle: 'filled',
      badge: 'Most Popular'
    },
    { 
      name: 'Agency', 
      robot: 'Unlimited Robots',
      monthlyPrice: '₹9,999',
      yearlyPrice: '₹99,990',
      description: 'Full power for agencies with white-label options and unlimited team access.',
      features: ['Unlimited leads', '200,000 AI tokens', 'All channels', 'White-label domains', 'API access', 'Dedicated support', 'Unlimited team'],
      cta: 'Get Agency',
      ctaStyle: 'filled'
    },
  ];

  const testimonials = [
    {
      name: 'Rahul Sharma',
      role: 'Founder, TechStartup',
      content: 'The AI robots literally work 24/7! We went from manually chasing leads to having everything automated. 3x more conversions!',
      rating: 5
    },
    {
      name: 'Priya Patel',
      role: 'Marketing Head, EduTech',
      content: 'I love how each robot specializes in different tasks. The automation robot handles our entire enrollment workflow flawlessly.',
      rating: 5
    },
    {
      name: 'Amit Kumar',
      role: 'CEO, Digital Agency',
      content: 'Our clients are amazed by the chatbot and white-label options. The analytics robot gives us insights we never had before.',
      rating: 5
    }
  ];

  const faqs = [
    { q: 'What are AI Robots?', a: 'Our AI Robots are specialized automation modules that handle different aspects of your marketing - lead capture, automation workflows, communication, and analytics. They work 24/7 so you don\'t have to!' },
    { q: 'How does the chatbot capture leads?', a: 'When visitors interact with the chat widget, they provide their name, email, and phone. This data is automatically saved to your CRM with AI scoring, helping you prioritize hot leads.' },
    { q: 'Can I customize the chatbot?', a: 'Yes! You can customize the chatbot\'s appearance, welcome message, and conversation flow. Agency plans include full white-label options with your branding.' },
    { q: 'Is WhatsApp integration included?', a: 'Yes! We support WhatsApp Business messaging for instant automated campaigns. Available on all plans with advanced features on Growth and Agency.' },
    { q: 'Do you offer refunds?', a: 'Absolutely! We offer a 30-day money-back guarantee. If you\'re not satisfied with our AI robots, contact support for a full refund.' },
  ];

  return (
    <div className="min-h-screen bg-[#031b14] overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#031b14]/80 border-b border-emerald-900/30">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <motion.div 
            className="flex items-center gap-3 cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <motion.img 
              src={ROBOTS.mascot} 
              alt="AI Lead Mantra" 
              className="w-10 h-10 object-contain drop-shadow-[0_0_10px_rgba(16,185,129,0.5)]"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="relative group">
              <span className="text-xl font-heading font-bold">
                <span className="text-white">AI</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-400 ml-1">Lead Mantra</span>
              </span>
              {/* Animated underline */}
              <motion.div 
                className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500"
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 1, delay: 0.5 }}
              />
              {/* Glow effect */}
              <div className="absolute -inset-2 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
            </div>
          </motion.div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#robots" className="text-emerald-100/80 hover:text-white transition-colors">AI Robots</a>
            <a href="#pricing" className="text-emerald-100/80 hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="text-emerald-100/80 hover:text-white transition-colors">FAQ</a>
          </div>
          <div className="flex gap-4 items-center">
            {/* Login Button - Pill-shaped outline */}
            <motion.button 
              data-testid="nav-login-btn"
              className="px-6 py-2.5 rounded-full border-2 border-white/80 text-white font-medium hover:bg-white/10 transition-all duration-300"
              onClick={() => { setShowAuth(true); setAuthMode('login'); }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Login
            </motion.button>
            
            {/* Book a FREE Demo Button - Animated with glow */}
            <motion.button 
              data-testid="nav-demo-btn"
              className="relative px-6 py-2.5 rounded-full bg-[#22c55e] text-white font-bold overflow-hidden group"
              onClick={() => setShowChatbot(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                boxShadow: [
                  '0 0 20px rgba(34, 197, 94, 0.4)',
                  '0 0 40px rgba(34, 197, 94, 0.6)',
                  '0 0 20px rgba(34, 197, 94, 0.4)',
                ],
              }}
              transition={{
                boxShadow: { duration: 2, repeat: Infinity, ease: "easeInOut" }
              }}
            >
              <span className="relative z-10">Book a FREE Demo</span>
              {/* Shine effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '200%' }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }}
              />
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Hero Section with Robot */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a5c44] via-[#063d2e] to-[#031b14]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-600/20 via-transparent to-transparent" />
        
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-emerald-400/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
                <Bot className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-300 text-sm font-medium">Meet Your AI Robot Team</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
                Let
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300"> AI Robots </span>
                Handle Your Marketing
              </h1>
              
              <p className="text-lg text-emerald-100/80 mb-8 max-w-lg">
                Deploy specialized AI robots that capture leads, automate follow-ups, manage conversations, and analyze performance - all on autopilot.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Button
                  data-testid="hero-cta-btn"
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-400 text-white shadow-xl shadow-emerald-500/30 px-8 py-6 text-lg group"
                  onClick={() => { setShowAuth(true); setAuthMode('signup'); }}
                >
                  Deploy Your Robots
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 px-8 py-6 text-lg"
                  onClick={() => setShowChatbot(true)}
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Chat with Aria
                </Button>
              </div>
            </motion.div>
            
            {/* Hero Robot */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative flex justify-center"
            >
              {/* Main Robot */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/30 to-teal-500/30 rounded-full blur-3xl scale-75" />
                <img
                  src={ROBOTS.leadCapture}
                  alt="AI Robot"
                  className="relative w-80 h-80 object-contain drop-shadow-2xl"
                />
              </motion.div>

              {/* Floating Mini Robots */}
              <motion.img
                src={ROBOTS.automation}
                alt="Automation Robot"
                className="absolute top-0 right-0 w-24 h-24 object-contain"
                animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
              />
              <motion.img
                src={ROBOTS.communication}
                alt="Communication Robot"
                className="absolute bottom-10 left-0 w-20 h-20 object-contain"
                animate={{ y: [0, 8, 0], rotate: [0, -5, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, delay: 1 }}
              />

              {/* Status Cards */}
              <motion.div
                className="absolute top-20 -left-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-3 shadow-xl"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">Lead Captured!</div>
                    <div className="text-emerald-300 text-xs">Score: 85/100</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="absolute bottom-0 right-0 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-3 shadow-xl"
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">Auto Follow-up</div>
                    <div className="text-emerald-300 text-xs">Sent via WhatsApp</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Robot Features Section */}
      <section id="robots" className="py-24 bg-gradient-to-b from-[#031b14] to-[#052e22]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Your <span className="text-emerald-400">AI Robot Team</span>
            </h2>
            <p className="text-emerald-100/70 max-w-2xl mx-auto">
              Each robot specializes in a different aspect of your marketing automation
            </p>
          </motion.div>
          
          <div className="space-y-16">
            {robotFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3 + index * 0.5, repeat: Infinity }}
                    className="relative flex justify-center"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-full blur-3xl scale-75" />
                    <img
                      src={feature.robot}
                      alt={feature.title}
                      className="relative w-72 h-72 object-contain drop-shadow-2xl"
                    />
                  </motion.div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-4">
                    <Bot className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-300 text-sm">{feature.subtitle}</span>
                  </div>
                  <h3 className="text-3xl font-heading font-bold text-white mb-4">{feature.title}</h3>
                  <p className="text-emerald-100/70 text-lg mb-6">{feature.description}</p>
                  <div className="flex flex-wrap gap-3">
                    {feature.highlights.map((highlight, i) => (
                      <span key={i} className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-300 text-sm flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* More Features Grid */}
      <section className="py-24 bg-[#031b14]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Powered by <span className="text-emerald-400">Advanced AI</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Layers />, title: 'Landing Pages', desc: '14+ Industry Templates' },
              { icon: <FlaskConical />, title: 'A/B Testing', desc: 'Auto-declare Winners' },
              { icon: <Globe />, title: 'Custom Domains', desc: 'White-label + SSL' },
              { icon: <Calendar />, title: 'Social Scheduling', desc: 'Multi-platform Posting' },
              { icon: <Palette />, title: 'Email Builder', desc: 'Drag & Drop Editor' },
              { icon: <BellRing />, title: 'Smart Alerts', desc: 'Real-time Notifications' },
              { icon: <LineChart />, title: 'Analytics', desc: 'Revenue & Conversions' },
              { icon: <CreditCard />, title: 'Payments', desc: 'Secure Payment Gateway' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
              >
                <Card className="p-5 bg-emerald-900/20 backdrop-blur border-emerald-800/30 hover:border-emerald-500/50 transition-all hover:shadow-lg hover:shadow-emerald-500/10 group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                  <p className="text-emerald-100/60 text-sm">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-[#031b14] to-[#052e22]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Choose Your Robot Team
            </h2>
            <p className="text-emerald-100/70 mb-8">
              Start free, upgrade anytime. Save 17% with yearly billing.
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-white/10 backdrop-blur rounded-full p-1.5 border border-white/20">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2.5 rounded-full font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-emerald-700'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annually')}
                className={`px-6 py-2.5 rounded-full font-medium transition-all ${
                  billingCycle === 'annually'
                    ? 'bg-white text-emerald-700'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Annually
              </button>
            </div>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`relative rounded-3xl p-8 h-full flex flex-col transition-all ${
                  plan.featured 
                    ? 'bg-white shadow-2xl shadow-black/20 scale-105 z-10' 
                    : 'bg-white/95 backdrop-blur border border-white/50 shadow-lg'
                }`}>
                  {plan.badge && (
                    <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {plan.badge}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                      <p className="text-sm text-emerald-600">{plan.robot}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">
                      {billingCycle === 'annually' ? plan.yearlyPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-gray-500 text-sm">/{billingCycle === 'annually' ? 'year' : 'month'}</span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">{plan.description}</p>
                  
                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <Check className="w-4 h-4 mr-3 flex-shrink-0 text-emerald-500" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    className={`w-full py-3 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                      plan.featured
                        ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                    onClick={() => { setShowAuth(true); setAuthMode('signup'); }}
                  >
                    {plan.cta}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gradient-to-b from-[#031b14] to-[#052e22]">
        <div className="container mx-auto px-6 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 bg-emerald-900/30 border border-emerald-800/30 rounded-xl hover:border-emerald-600/50 transition-colors text-left"
                >
                  <span className="text-white font-medium pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-emerald-400 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-5 pb-5 pt-3 bg-emerald-900/20 border-x border-b border-emerald-800/30 rounded-b-xl -mt-1"
                  >
                    <p className="text-emerald-100/70">{faq.a}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-emerald-700 to-teal-600 relative overflow-hidden">
        {/* Background robots */}
        <div className="absolute inset-0 opacity-10">
          <img src={ROBOTS.automation} alt="" className="absolute left-10 top-10 w-40 h-40 object-contain" />
          <img src={ROBOTS.analytics} alt="" className="absolute right-10 bottom-10 w-40 h-40 object-contain" />
        </div>
        
        <div className="container mx-auto px-6 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.img 
              src={ROBOTS.mascot} 
              alt="AI Robot" 
              className="w-28 h-28 mx-auto mb-6"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Ready to Deploy Your AI Robots?
            </h2>
            <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join 500+ businesses already using AI robots to automate their marketing and 10x their lead generation.
            </p>
            <Button
              size="lg"
              className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-xl px-8 py-6 text-lg"
              onClick={() => { setShowAuth(true); setAuthMode('signup'); }}
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-emerald-200/80 text-sm mt-4">No credit card required • 14-day free trial • Cancel anytime</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#021510] py-12 border-t border-emerald-900/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={ROBOTS.mascot} alt="AI Lead Mantra" className="w-8 h-8" />
                <span className="text-white font-heading font-bold">AI Lead Mantra</span>
              </div>
              <p className="text-emerald-300/60 text-sm">
                AI-powered robots that automate your entire marketing workflow.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-emerald-300/70 text-sm">
                <li><a href="#robots" className="hover:text-white transition-colors">AI Robots</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Templates</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-emerald-300/70 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="mailto:hello@aileadmantra.com" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-emerald-300/70 text-sm">
                <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="/refund" className="hover:text-white transition-colors">Refund Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-emerald-900/30">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-center md:text-left">
                <p className="text-white font-semibold">© 2026 AILeadMantra</p>
                <p className="text-emerald-300/50 text-sm">B2B SaaS Platform</p>
              </div>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <a href="/" className="text-emerald-300/70 hover:text-white transition-colors">Home</a>
                <span className="text-emerald-800">|</span>
                <a href="#robots" className="text-emerald-300/70 hover:text-white transition-colors">Features</a>
                <span className="text-emerald-800">|</span>
                <a href="#pricing" className="text-emerald-300/70 hover:text-white transition-colors">Pricing</a>
                <span className="text-emerald-800">|</span>
                <a href="mailto:hello@aileadmantra.com" className="text-emerald-300/70 hover:text-white transition-colors">Contact</a>
              </div>
              <div className="flex gap-4 text-sm">
                <a href="/terms" className="text-emerald-300/70 hover:text-white transition-colors">Terms</a>
                <span className="text-emerald-800">|</span>
                <a href="/privacy" className="text-emerald-300/70 hover:text-white transition-colors">Privacy</a>
                <span className="text-emerald-800">|</span>
                <a href="/refund" className="text-emerald-300/70 hover:text-white transition-colors">Refund Policy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Chatbot FAB Button */}
      <motion.button
        onClick={() => setShowChatbot(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-[#0a5c44] to-[#0d7c5c] rounded-full shadow-2xl flex items-center justify-center z-40 group"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {chatbotPulse && !showChatbot && (
          <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-30"></span>
        )}
        <img src={ROBOTS.mascot} alt="Chat" className="w-10 h-10 object-contain" />
      </motion.button>

      {/* Chatbot Widget */}
      <AnimatePresence>
        {showChatbot && (
          <ChatbotWidget 
            onClose={() => setShowChatbot(false)}
            onSubmit={(data) => console.log('Lead captured:', data)}
          />
        )}
      </AnimatePresence>

      {/* Auth Modal */}
      {showAuth && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowAuth(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0a3d2e] border border-emerald-700/50 rounded-2xl p-8 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <motion.img 
                src={ROBOTS.mascot} 
                alt="AI Lead Mantra" 
                className="w-20 h-20 mx-auto mb-4"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <h3 className="text-2xl font-heading font-bold text-white">
                {authMode === 'login' ? 'Welcome Back!' : 'Deploy Your Robots'}
              </h3>
              <p className="text-emerald-300/70 text-sm mt-1">
                {authMode === 'login' ? 'Sign in to your robot control center' : 'Start your 14-day free trial'}
              </p>
            </div>
            
            <Button
              data-testid="modal-google-btn"
              className="w-full mb-4 bg-white text-gray-700 hover:bg-gray-100 flex items-center justify-center gap-2"
              onClick={handleGoogleLogin}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-emerald-700/50"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#0a3d2e] text-emerald-300/70">or continue with email</span>
              </div>
            </div>
            
            <form onSubmit={handleEmailAuth} className="space-y-4">
              {authMode === 'signup' && (
                <input
                  type="text"
                  placeholder="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 bg-emerald-900/30 border border-emerald-700/50 rounded-lg text-white placeholder-emerald-300/50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                />
              )}
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-emerald-900/30 border border-emerald-700/50 rounded-lg text-white placeholder-emerald-300/50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-emerald-900/30 border border-emerald-700/50 rounded-lg text-white placeholder-emerald-300/50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3"
                disabled={loading}
              >
                {loading ? 'Please wait...' : (authMode === 'login' ? 'Sign In' : 'Create Account')}
              </Button>
            </form>
            
            <p className="text-center text-emerald-300/70 text-sm mt-6">
              {authMode === 'login' ? "Don't have an account?" : "Already have an account?"}
              <button
                className="text-emerald-400 hover:text-emerald-300 ml-1 font-medium"
                onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
              >
                {authMode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default LandingPage;
